import { useEffect, createContext, ReactNode, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { PublicClientApplication, EventType, AuthenticationResult } from '@azure/msal-browser'

import msGraphApi from '@/utils/api/msGraphApi'
import AccessRoleFilter from '@/utils/accessRoleFilter'

import { RootState } from '@/utils/store/store'
import { updateConfig } from '@/utils/store/features/application/applicationSlice'
import { updateAuthenticationInformation, updateAuthorisationFlow, updateAuthenticationState, resetAuthenticationState } from '@/utils/store/features/authentication/authenticationSlice'
import useGenericStore from '@/hooks/useGenericStore'
import useConsent from '@/hooks/useConsent'

import { AuthorisationStates } from '@/constants'

import { AuthenticationContextType, UserInformation, TokenInformation, RehydrateData } from '@/shared/types/authentication'
import { Config } from '@/shared/types/config'

interface ErrorDetails {
    message?: string
    errorCode?: string
    code?: string
}

const getErrorDetails = (error: unknown): ErrorDetails => {
    if (typeof error !== 'object' || error === null) return {}

    return {
        message: 'message' in error && typeof error.message === 'string' ? error.message : undefined,
        errorCode: 'errorCode' in error && typeof error.errorCode === 'string' ? error.errorCode : undefined,
        code: 'code' in error && typeof error.code === 'string' ? error.code : undefined,
    }
}

const AuthenticationContext = createContext<AuthenticationContextType>({
    isAuthInitialized: false,
    authenticationState: '',
    authorisationFlow: '',
    information: {},
    loginUser: () => { },
    logout: () => { },
    rehydrateInformation: async () => undefined,
    getGraphToken: async () => '',
    getApiToken: async () => '',
    getAccessToken: async () => '',
    getIdToken: async () => '',
    validateToken: async () => false,
    validateTokenAndRehydrate: async () => false,
})

const AuthenticationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { rawConfig } = useSelector((state: RootState) => state.application)
    const { information, authorisationFlow, authenticationState } = useSelector((state: RootState) => state.authentication)
    const dispatch = useDispatch()
    const { setGenericStoreValue, getGenericStoreValue, removeGenericStoreValue } = useGenericStore()
    const { hasUserRespondedToConsent, isConsentInitialized } = useConsent()

    const msalInstanceRef = useRef<PublicClientApplication | null>(null)
    const msalInitializedRef = useRef(false)
    const redirectInitStartedRef = useRef(false)
    const isAuthInitializedRef = useRef(false)

    const isLoginInteractionInProgress = () => {
        return sessionStorage.getItem('isAuthorizationInProgress') === 'true'
    }

    const clearLoginInteraction = () => {
        sessionStorage.removeItem('isAuthorizationInProgress')
    }

    const requestRelogin = () => {
        clearLoginInteraction()
        dispatch(updateAuthorisationFlow(AuthorisationStates.RELOGIN))
    }

    // Local-dev safeguard: the DOCK consent web component frequently never emits a
    // `dock-consent-changed` event on localhost, which would otherwise deadlock auth
    // initialization (infinite loading, no login screen). Bypass the consent gate when
    // running the local dev server.
    const isLocalDevEnvironment =
        import.meta.env.MODE === 'development' &&
        ['localhost', '127.0.0.1'].includes(window.location.hostname)
    const consentGateSatisfied = hasUserRespondedToConsent || isLocalDevEnvironment

    const getApiScopes = () => {
        const configuredScopes = rawConfig?.authentication?.loginRequest?.scopes || []
        if (configuredScopes.length) return configuredScopes

        const clientId = rawConfig?.authentication?.msalConfig?.clientId
        return clientId ? [`api://${clientId}/.default`] : []
    }

    const ensureMsalInstance = async () => {
        const msalConfig = rawConfig?.authentication?.msalConfig
        if (!msalConfig) throw new Error('Missing MSAL configuration')

        if (!msalInstanceRef.current) {
            msalInstanceRef.current = new PublicClientApplication({
                auth: { ...msalConfig, redirectUri: window.location.origin },
                cache: {
                    cacheLocation: 'sessionStorage',
                    storeAuthStateInCookie: false,
                },
                system: {
                    allowRedirectInIframe: false,
                },
            })
        }

        if (!msalInitializedRef.current) {
            await msalInstanceRef.current.initialize()
            msalInitializedRef.current = true
        }

        return msalInstanceRef.current
    }

    const [isAuthInitialized, setIsAuthInitialized] = useState(false)
    const redirectDidAuthenticateRef = useRef(false)

    // Allow auth initialization when user responds to consent
    useEffect(() => {
        if (isConsentInitialized && consentGateSatisfied) {
            // User has responded to consent (accept or decline) - allow auth initialization
            redirectInitStartedRef.current = false
        }
    }, [consentGateSatisfied, isConsentInitialized])

    useEffect(() => {
        if (!rawConfig?.authentication?.msalConfig) return
        if (!isConsentInitialized) return // Wait for consent to be loaded
        if (!consentGateSatisfied) return // Skip MSAL init if user has not responded to consent
        if (redirectInitStartedRef.current) return
        redirectInitStartedRef.current = true

        let isMounted = true
        let callbackId: string | null = null

        ;(async () => {
            try {
                const msalInstance = await ensureMsalInstance()
                callbackId = msalInstance.addEventCallback(onInstanceEvent)

                const response = await msalInstance.handleRedirectPromise()
                if (response) {
                    clearLoginInteraction()
                    redirectDidAuthenticateRef.current = true
                    await extractUserInformation(response)
                    dispatch(updateAuthorisationFlow(AuthorisationStates.AUTHORISED))
                    removeGenericStoreValue('disableAutoAuthorize', true)
                } else {
                    const cachedAccount = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0]
                    if (!cachedAccount) {
                        requestRelogin()
                    } else {
                        msalInstance.setActiveAccount(cachedAccount)
                        try {
                            await extractUserInformation({ account: cachedAccount })
                            dispatch(updateAuthorisationFlow(AuthorisationStates.AUTHORISED))
                            removeGenericStoreValue('disableAutoAuthorize', true)
                        } catch {
                            requestRelogin()
                        }
                    }
                }
            } catch (error: unknown) {
                const errorDetails = getErrorDetails(error)
                clearLoginInteraction()
                dispatch(updateAuthenticationState({
                    message: errorDetails.message,
                    code: errorDetails.errorCode || errorDetails.code,
                }))
            } finally {
                if (isMounted) {
                    isAuthInitializedRef.current = true
                    setIsAuthInitialized(true)
                }
            }
        })()

        return () => {
            isMounted = false
            const msalInstance = msalInstanceRef.current
            if (msalInstance && callbackId) msalInstance.removeEventCallback(callbackId)
        }
        // the functions are created again on every render: the initialization runs once for the MSAL config and the consent
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawConfig?.authentication?.msalConfig, isConsentInitialized, consentGateSatisfied])

    // Auto-trigger login after user responds to consent and auth is initialized
    useEffect(() => {
        if (isAuthInitialized &&
            consentGateSatisfied &&
            authorisationFlow === AuthorisationStates.RELOGIN &&
            !information?.userId &&
            !isLoginInteractionInProgress() &&
            !getGenericStoreValue('disableAutoAuthorize')) {

            loginUser()
        }
        // loginUser is created again on every render: the login starts when the state of the sign-in changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthInitialized, consentGateSatisfied, authorisationFlow, information?.userId, getGenericStoreValue])

    const onInstanceEvent = (event: { eventType: EventType }) => {
        if (!isAuthInitializedRef.current) return
        switch (event.eventType) {
            case EventType.ACQUIRE_TOKEN_FAILURE:
            case EventType.SSO_SILENT_FAILURE:
                break
        }
    }

    const loginUser = async () => {
        if (!rawConfig) return
        if (isLoginInteractionInProgress()) return

        sessionStorage.setItem('isAuthorizationInProgress', 'true')
        dispatch(updateAuthorisationFlow(AuthorisationStates.AUTHORISING))

        try {
            const msalInstance = await ensureMsalInstance()
            await msalInstance.loginRedirect(rawConfig.authentication.loginRequest)
        } catch (error: unknown) {
            const errorDetails = getErrorDetails(error)
            if (errorDetails.errorCode === 'interaction_in_progress') {
                return
            }

            clearLoginInteraction()
            dispatch(updateAuthenticationState({
                message: errorDetails.message,
                code: errorDetails.errorCode || errorDetails.code,
            }))
            dispatch(updateAuthorisationFlow(AuthorisationStates.RELOGIN))
        }
    }

    const logout = async () => {
        clearLoginInteraction()
        dispatch(resetAuthenticationState())
        setGenericStoreValue('disableAutoAuthorize', true, true)

        try {
            const msalInstance = msalInstanceRef.current
            if (msalInstance && msalInitializedRef.current) {
                await msalInstance.clearCache()
            }
        } catch {
            console.warn('Error clearing MSAL cache during logout')
        }
    }

    const validateToken = async () => {
        try {
            await getIdToken()
            return true
        } catch {
            return false
        }
    }

    const validateTokenAndRehydrate = async (dataToPersist?: RehydrateData) => {
        const tokenValid = await validateToken()

        if (!tokenValid && dataToPersist) {
            const existingData = getGenericStoreValue<RehydrateData>('dataToRehydrate') ?? {}
            setGenericStoreValue('dataToRehydrate', { ...existingData, ...dataToPersist })
        }

        return tokenValid
    }

    const rehydrateInformation = async () => {
        const msalInstance = await ensureMsalInstance()

        if (rawConfig) {
            const cachedAccount = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0]

            if (!cachedAccount) {
                requestRelogin()
                return undefined
            }

            msalInstance.setActiveAccount(cachedAccount)

            try {
                const response = await msalInstance.acquireTokenSilent({
                    ...rawConfig.authentication.loginRequest,
                    account: cachedAccount,
                    forceRefresh: true,
                })
                return await extractUserInformation(response)
            } catch (error: unknown) {
                if (getErrorDetails(error).errorCode === 'interaction_in_progress') return undefined
                requestRelogin()
                return undefined
            }
        }
    }

    const extractUserInformation = async (rawInformation: Partial<AuthenticationResult>) => {
        let userInformation: UserInformation = {}

        let account: AuthenticationResult['account'] | undefined
        let tokenInformation: TokenInformation | undefined = undefined
        let profilePhoto: string | null | undefined

        const msalInstance = await ensureMsalInstance()

        // First, check if we have an account from rawInformation or MSAL cache
        if (rawInformation.account) {
            account = rawInformation.account
            // Set this as the active account in MSAL before trying to get tokens
            msalInstance.setActiveAccount(account)
        } else {
            // If no account in rawInformation, check MSAL cache
            const accounts = msalInstance.getAllAccounts()
            if (accounts.length === 0) {
                requestRelogin()
                return userInformation
            }
            account = accounts[0]
            msalInstance.setActiveAccount(account)
        }

        const photoPromise: Promise<string | null | undefined> = account.idTokenClaims?.oid
            ? getGraphToken().then(t => msGraphApi.getUserPhoto(t)).catch(() => undefined)
            : Promise.resolve(undefined)

        try {
            tokenInformation = await getTokenInformation(false)
        } catch (error: unknown) {
            if (getErrorDetails(error).errorCode === 'interaction_in_progress') return undefined
            requestRelogin()
            return undefined
        }

        if (account && tokenInformation) {
            userInformation = {}
            profilePhoto = await photoPromise

            if (account.tenantProfiles) {
                delete account.tenantProfiles
            }

            userInformation.userId = account.idTokenClaims?.oid
            
            const roles = account.idTokenClaims?.roles || []
            
            userInformation.roles = roles
            userInformation.account = account
            userInformation.profilePhoto = profilePhoto
            userInformation = { ...userInformation, ...tokenInformation }

            const filteredConfig = filterAssistants(rawConfig, userInformation.roles || [])

            dispatch(updateConfig({ config: filteredConfig }))
            dispatch(updateAuthenticationInformation(userInformation))
        }

        return userInformation
    }

    // Get token for MS Graph API (profile photo, etc.)
    const getGraphToken = async () => {
        const msalInstance = await ensureMsalInstance()
        const graphScopes = ['ProfilePhoto.Read.All'] // or required Graph scopes
        const account = msalInstance.getAllAccounts()[0]
        const response = await msalInstance.acquireTokenSilent({
            scopes: graphScopes,
            account,
        })
        return response.accessToken
    }

    // Get token for backend API (api scope)
    const getApiToken = async (forceRefresh = false) => getAccessToken(forceRefresh)

    const filterAssistants = (config: Config | undefined, roles: string[]) => {
        let filteredConfig = { ...config }
        let filteredAssistants = []

        if (roles) {
            filteredAssistants = AccessRoleFilter.filterAssistants(roles, config?.assistants)
            filteredConfig = { ...config, assistants: filteredAssistants }
        }

        return filteredConfig
    }

    const acquireApiTokenResult = async (forceRefresh = false) => {
        const msalInstance = await ensureMsalInstance()

        // Always check active account first
        let account = msalInstance.getActiveAccount()

        // If no active account, check cache
        if (!account) {
            const accounts = msalInstance.getAllAccounts()
            if (accounts.length > 0) {
                account = accounts[0]
                msalInstance.setActiveAccount(account)
            }
        }
        
        if (!account) {
            requestRelogin()
            throw new Error('No account available')
        }

        const apiScopes = getApiScopes()
        if (!apiScopes.length) throw new Error('No API scopes configured')

        try {
            return await msalInstance.acquireTokenSilent({
                scopes: apiScopes,
                account,
                forceRefresh,
            })
        } catch (err: unknown) {
            const errorCode = getErrorDetails(err).errorCode
            const requiresInteraction =
                errorCode === 'interaction_required' ||
                errorCode === 'login_required' ||
                errorCode === 'consent_required'
            if (requiresInteraction) requestRelogin()
            throw err
        }
    }

    const getTokenInformation = async (forceRefresh = false): Promise<TokenInformation> => {
        const response = await acquireApiTokenResult(forceRefresh)
        const expirationClaim = response.idTokenClaims &&
            'exp' in response.idTokenClaims &&
            typeof response.idTokenClaims.exp === 'number'
            ? response.idTokenClaims.exp
            : undefined

        return {
            accessToken: response.accessToken,
            expiresOn: response.expiresOn?.getTime(), // Convert Date to timestamp (number)
            idToken: response.idToken,
            idTokenClaims: { exp: expirationClaim },
        }
    }

    const getAccessToken = async (forceRefresh = false): Promise<string> => {
        const response = await acquireApiTokenResult(forceRefresh)
        if (!response.accessToken) throw new Error('No access token returned')
        return response.accessToken
    }

    const getIdToken = async (forceRefresh = false): Promise<string> => {
        const response = await acquireApiTokenResult(forceRefresh)
        if (!response.idToken) throw new Error('No id token returned')
        return response.idToken
    }

    return (
        <AuthenticationContext.Provider value={{
            isAuthInitialized,
            information,
            authorisationFlow,
            authenticationState,
            loginUser,
            logout,
            rehydrateInformation,
            getGraphToken,
            getApiToken,
            getAccessToken,
            getIdToken,
            validateToken,
            validateTokenAndRehydrate,
        }}>
            {children}
        </AuthenticationContext.Provider>
    )
}

export { AuthenticationProvider, AuthenticationContext, AuthorisationStates }
